// Asset profiles that the AnalyticsEngineV2 provisions for market-data
// purposes (wholesale electricity prices), NOT for measurement-point
// purposes. They show up alongside operator measurement points but should
// never appear in:
//
//   - hierarchy trees (they have no Contains-children measurement points)
//   - entity-search dropdowns (operators don't drill into a zone the way
//     they drill into a site; the only useful surface for zones is the
//     /market-price page)
//   - "auto-pick first asset" code paths that seed a tree root for an
//     operator-facing route
//
// Centralised here so all consumers reach for the same exclusion list —
// when AE2 adds a new market-data asset profile (e.g. carbon intensity,
// natural gas markets, demand-response programmes), one edit propagates.
//
// Note: the `type` field on TB entities is the asset PROFILE name, not
// the entity type (ASSET/DEVICE/etc). The constants below match the
// profile names emitted by AE2's market_provisioner.

export const MARKET_DATA_ASSET_TYPES: readonly string[] = [
  "electricity_bidding_zone",
  // AE2 also provisions a parent container labelled "Energy Markets" with
  // this profile; exclude it from operator pickers too — it has no
  // measurement-point children, only zones.
  "electricity_market_price_root",
];

const MARKET_SET: ReadonlySet<string> = new Set(MARKET_DATA_ASSET_TYPES);

/** True when `asset.type` is one of the AnalyticsEngine's market-data
 *  asset profiles. Safe to call with null/undefined. */
export function isMarketDataAsset(
  asset: { type?: string } | null | undefined,
): boolean {
  return Boolean(asset?.type && MARKET_SET.has(asset.type));
}

/** Drop market-data assets from a list. Preserves order. Returns a new
 *  array (does not mutate). Use at every site that auto-picks a tree
 *  root or surfaces tenant assets in a measurement-point picker. */
export function filterOutMarketDataAssets<T extends { type?: string }>(
  assets: readonly T[] | undefined | null,
): T[] {
  if (!assets || assets.length === 0) return [];
  return assets.filter((a) => !isMarketDataAsset(a));
}

/** Bidding-zone specifically. The price page never lists the root container. */
export const BIDDING_ZONE_ASSET_TYPE = "electricity_bidding_zone";
